// Tutor RosterPage — course roster with a per-student check-in action.
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch, postJson } from "../../hooks/useApi.js";
import PaymentStatusBadge from "../../components/shared/PaymentStatusBadge.js";
import type { Course, Enrollment, PaginatedResult } from "@dm-lms/shared/types";

// TODO: replace with a real session picker once session scheduling exists —
// check-in currently always targets this placeholder session.
const PLACEHOLDER_SESSION_ID = "00000000-0000-0000-0000-000000000000";

export default function RosterPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { data: coursesData } = useFetch<PaginatedResult<Course>>("/api/courses?page=1&pageSize=100");
  const myCourses = useMemo(
    () => (coursesData?.items ?? []).filter((course) => course.tutorId === user?.id),
    [coursesData, user]
  );

  const [courseId, setCourseId] = useState(() => searchParams.get("courseId") ?? "");
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useFetch<PaginatedResult<Enrollment>>(
    courseId ? `/api/enrollments/course/${courseId}?page=${page}&pageSize=20` : null
  );

  const [checkedIn, setCheckedIn] = useState<Set<string>>(new Set());
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [checkInErrors, setCheckInErrors] = useState<Record<string, string>>({});

  function selectCourse(id: string) {
    setCourseId(id);
    setPage(1);
  }

  async function checkIn(studentId: string) {
    setCheckingInId(studentId);
    setCheckInErrors((prev) => ({ ...prev, [studentId]: "" }));

    const result = await postJson<null>("/api/attendance/check-in", {
      studentId,
      sessionId: PLACEHOLDER_SESSION_ID,
    });
    setCheckingInId(null);
    if (!result.ok) {
      setCheckInErrors((prev) => ({ ...prev, [studentId]: result.error }));
      return;
    }
    setCheckedIn((prev) => new Set(prev).add(studentId));
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Roster</h1>

      <label className="block text-xs text-dm-slate mb-1">Course</label>
      <select
        className="w-full sm:w-96 border border-gray-300 rounded-md px-3 py-2 text-sm mb-6"
        value={courseId}
        onChange={(e) => selectCourse(e.target.value)}
      >
        <option value="">Select a course</option>
        {myCourses.map((course) => (
          <option key={course.id} value={course.id}>{course.code} — {course.title}</option>
        ))}
      </select>

      {!courseId && <p className="text-dm-slate text-sm">Select a course to view its roster.</p>}
      {courseId && isLoading && <p className="text-dm-slate text-sm">Loading roster…</p>}
      {courseId && error && <p className="text-red-600 text-sm">{error}</p>}

      {courseId && !isLoading && !error && data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-dm-slate text-sm">No students enrolled in this course yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Student ID</th>
                    <th className="px-4 py-3 font-medium">Payment</th>
                    <th className="px-4 py-3 font-medium">Enrolled</th>
                    <th className="px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.items.map((enrollment) => {
                    const isChecked = checkedIn.has(enrollment.studentId);
                    const isChecking = checkingInId === enrollment.studentId;
                    const rowError = checkInErrors[enrollment.studentId];
                    return (
                      <tr key={enrollment.id}>
                        <td className="px-4 py-3">{enrollment.studentId}</td>
                        <td className="px-4 py-3"><PaymentStatusBadge status={enrollment.paymentStatus} /></td>
                        <td className="px-4 py-3">{new Date(enrollment.enrolledAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          {isChecked ? (
                            <span className="text-xs text-green-700 font-medium">Checked in</span>
                          ) : (
                            <button
                              type="button"
                              className="text-xs px-3 py-1.5 rounded-md bg-dm-blue text-white disabled:opacity-50"
                              disabled={isChecking}
                              onClick={() => checkIn(enrollment.studentId)}
                            >
                              {isChecking ? "Checking in…" : "Check In"}
                            </button>
                          )}
                          {rowError && <p className="text-red-600 text-xs mt-1">{rowError}</p>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-xs text-dm-slate">Page {data.page} · {data.total} total</span>
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={!data.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
